import React, { useMemo } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Pencil, Trash2 } from 'lucide-react';
import {
  formatHourLabel12h,
  formatTimeRange12h,
  getTimeGridBounds,
  timeToMinutes,
} from '../../utils/timeFormat';

const HOUR_PX = 56;
const AXIS_INSET = 10;

const DAY_SHORT = {
  monday: 'Mon',
  tuesday: 'Tue',
  wednesday: 'Wed',
  thursday: 'Thu',
  friday: 'Fri',
  saturday: 'Sat',
};

function layoutOverlapping(entries, gridStartMin) {
  const sorted = [...entries]
    .filter((e) => timeToMinutes(e.start_time) != null && timeToMinutes(e.end_time) != null)
    .sort((a, b) => {
      const sa = timeToMinutes(a.start_time);
      const sb = timeToMinutes(b.start_time);
      return sa - sb;
    });

  const columns = [];
  return sorted.map((entry) => {
    const startMin = timeToMinutes(entry.start_time);
    const endMin = timeToMinutes(entry.end_time);
    let col = 0;
    while (columns[col] != null && columns[col] > startMin) {
      col += 1;
    }
    columns[col] = endMin;
    const overlapCount = columns.filter((end) => end != null && end > startMin).length;
    return {
      entry,
      top: ((startMin - gridStartMin) / 60) * HOUR_PX,
      height: Math.max(((endMin - startMin) / 60) * HOUR_PX, 24),
      col,
      overlapCount: Math.max(overlapCount, col + 1),
    };
  });
}

function isTodayWeekday(dayKey) {
  const map = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  return map[new Date().getDay()] === dayKey;
}

function PeriodBlock({ entry, height, canManage, onEdit, onDelete }) {
  const title = [entry.class_name, entry.subject_name].filter(Boolean).join(' ');
  const subtitle = [
    formatTimeRange12h(entry.start_time, entry.end_time),
    entry.section_name ? `Sec ${entry.section_name}` : '',
    entry.room || '',
  ].filter(Boolean).join(' · ');

  const showSubtitle = height >= 48;
  const showButtons = canManage && height >= 64;
  const compactClick = canManage && height < 48;

  const titleClass = height < 32
    ? 'text-xs leading-snug'
    : height < 56
      ? 'text-sm leading-snug'
      : 'text-sm leading-tight';

  const paddingClass = height < 64 ? 'px-1.5 py-0.5' : 'px-2 py-1';
  const layoutClass = height < 48 ? 'justify-center' : 'justify-start';

  return (
    <div
      className={`h-full rounded-md border-l-4 border-primary bg-primary/15 flex flex-col min-h-0 ${paddingClass} ${layoutClass} ${compactClick ? 'cursor-pointer' : ''}`}
      onClick={compactClick ? () => onEdit?.(entry) : undefined}
      onKeyDown={compactClick ? (e) => { if (e.key === 'Enter') onEdit?.(entry); } : undefined}
      role={compactClick ? 'button' : undefined}
      tabIndex={compactClick ? 0 : undefined}
    >
      <div className={`min-w-0 ${showButtons ? 'flex-1' : ''}`}>
        <p className={`font-medium text-foreground truncate ${titleClass}`}>
          {title || 'Period'}
        </p>
        {showSubtitle && (
          <p className="text-[11px] text-muted-foreground truncate mt-0.5 leading-snug">{subtitle}</p>
        )}
      </div>
      {showButtons && (
        <div className="flex items-center justify-end gap-0.5 shrink-0 mt-0.5">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => onEdit?.(entry)}
            aria-label="Edit"
          >
            <Pencil className="w-3 h-3" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-red-400"
            onClick={() => onDelete?.(entry)}
            aria-label="Delete"
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      )}
    </div>
  );
}

export default function TeacherDayCalendar({
  entries = [],
  selectedDay,
  onDayChange,
  weekDays = [],
  canManage = false,
  onEdit,
  onDelete,
}) {
  const { startHour, endHour } = useMemo(() => getTimeGridBounds(entries), [entries]);
  const hours = useMemo(() => {
    const list = [];
    for (let h = startHour; h <= endHour; h += 1) list.push(h);
    return list;
  }, [startHour, endHour]);

  const gridStartMin = startHour * 60;
  const gridHeight = (endHour - startHour) * HOUR_PX;
  const totalHeight = gridHeight + AXIS_INSET * 2;
  const laidOut = useMemo(
    () => layoutOverlapping(entries, gridStartMin),
    [entries, gridStartMin],
  );

  const nowLineTop = useMemo(() => {
    if (!isTodayWeekday(selectedDay)) return null;
    const now = new Date();
    const nowMin = now.getHours() * 60 + now.getMinutes();
    if (nowMin < gridStartMin || nowMin > endHour * 60) return null;
    return AXIS_INSET + ((nowMin - gridStartMin) / 60) * HOUR_PX;
  }, [selectedDay, gridStartMin, endHour]);

  return (
    <Card className="border-border">
      <CardContent className="p-0">
        <div className="flex flex-wrap gap-1 p-3 border-b border-border bg-muted/30 overflow-hidden rounded-t-lg">
          {weekDays.map((day) => {
            const active = day === selectedDay;
            return (
              <button
                key={day}
                type="button"
                onClick={() => onDayChange(day)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  active
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                {DAY_SHORT[day] || day.charAt(0).toUpperCase() + day.slice(1, 3)}
              </button>
            );
          })}
        </div>

        <div className="max-h-[70vh] overflow-y-auto">
          <div className="flex min-h-[200px]">
            <div
              className="w-16 shrink-0 flex flex-col"
              style={{ height: totalHeight, paddingTop: AXIS_INSET, paddingBottom: AXIS_INSET }}
            >
              {hours.map((h) => (
                <div key={h} className="relative flex-shrink-0" style={{ height: HOUR_PX }}>
                  <span className="absolute right-2 top-0 -translate-y-1/2 text-[11px] leading-none text-muted-foreground tabular-nums whitespace-nowrap">
                    {formatHourLabel12h(h)}
                  </span>
                </div>
              ))}
            </div>

            <div
              className="flex-1 relative border-l border-border"
              style={{ height: totalHeight, paddingTop: AXIS_INSET, paddingBottom: AXIS_INSET }}
            >
              {hours.map((h) => (
                <div
                  key={`line-${h}`}
                  className="absolute left-0 right-0 border-t border-border/80"
                  style={{ top: AXIS_INSET + (h - startHour) * HOUR_PX }}
                />
              ))}

              {nowLineTop != null && (
                <div
                  className="absolute left-0 right-0 z-20 flex items-center pointer-events-none"
                  style={{ top: nowLineTop }}
                >
                  <span className="w-2 h-2 rounded-full bg-red-500 -ml-1 shrink-0" />
                  <span className="flex-1 h-px bg-red-500" />
                </div>
              )}

              {laidOut.length === 0 ? (
                <p className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
                  No classes scheduled
                </p>
              ) : (
                laidOut.map(({ entry, top, height, col, overlapCount }) => {
                  const widthPct = 100 / overlapCount;
                  const leftPct = col * widthPct;

                  return (
                    <div
                      key={entry.id}
                      className="absolute z-10 px-1"
                      style={{
                        top: AXIS_INSET + top,
                        height,
                        left: `${leftPct}%`,
                        width: `${widthPct}%`,
                      }}
                    >
                      <PeriodBlock
                        entry={entry}
                        height={height}
                        canManage={canManage}
                        onEdit={onEdit}
                        onDelete={onDelete}
                      />
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
